import { type Dispatch, type FC, type SetStateAction, useMemo } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Stars01Icon } from '@/components/ui/icons'
import { useGalleryAiVettingShortcuts } from '@/features/photoset/hooks/use-gallery-ai-vetting-shortcuts'
import { useImageSelectionApproval } from '@/features/photoset/queries/use-image-selection-approval'
import type { Image, PhotosetSpace, Space } from '@/features/photoset/types'
import { spaceNameToLabel } from '@/features/photoset/utils'
import { errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface GalleryAiVettingModalProps {
	selectableImages: Omit<Image, 'space'>[]
	currentDetailImageIndex: number
	setCurrentDetailImageIndex: Dispatch<SetStateAction<number | null>>
	currentDetailSpaceIndex: number
	onClose: () => void
	currentSpaceId: PhotosetSpace['id']
	currentSpaceName: Space['key_name']
}

export const GalleryAiVettingModal: FC<GalleryAiVettingModalProps> = ({
	selectableImages,
	currentDetailImageIndex,
	setCurrentDetailImageIndex,
	currentDetailSpaceIndex,
	onClose,
	currentSpaceName
}) => {
	const { t } = useTranslation()
	const currentImage = useMemo(
		() => selectableImages[currentDetailImageIndex],
		[currentDetailImageIndex, selectableImages]
	)

	const { mutate: imageSelectionApprovalToggle } = useImageSelectionApproval({
		photosetId: currentImage.photoset_id,
		photosetSpaceId: currentImage.photoset_space_id as number,
		options: {
			onError: (error) => errorHandler(error)
		}
	})

	const onToggleSelection = () => {
		imageSelectionApprovalToggle({
			imageId: currentImage.id,
			need_manual_retouching: !currentImage.need_manual_retouching
		})
	}

	useGalleryAiVettingShortcuts({
		currentImage,
		currentSpaceIndex: currentDetailSpaceIndex,
		selectableImages,
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		onToggleSelection,
		onClose
	})

	return (
		<GalleryModal
			currentImage={currentImage}
			headerSlot={
				<GalleryModalHeader
					currentImage={currentImage}
					endSlot={
						<ButtonLegacy
							onClick={(e) => {
								onToggleSelection()
								e.currentTarget.blur()
							}}
							tabIndex={-1}
							type='button'
							variant='secondary'
						>
							<Checkbox
								checked={currentImage.need_manual_retouching}
								readOnly
								tabIndex={-1}
							/>
							{t('common:select_space')}
						</ButtonLegacy>
					}
					iconTitle={<Stars01Icon size={20} />}
					onClose={onClose}
					subtitle={`${spaceNameToLabel[currentSpaceName]} — ${t('common:x_images_selected', { count: 1 })}`}
				/>
			}
			onClose={onClose}
			thumbnails={currentImage.ai_retouched_thumbnails}
			thumbnailsSlot={
				<GalleryModalThumbnails
					currentImageId={currentImage.id}
					images={selectableImages}
					onSelect={(_, index) => {
						setCurrentDetailImageIndex(index)
					}}
					thumbnails={'ai_retouched_thumbnails'}
				/>
			}
		/>
	)
}
